import { useState, useEffect } from 'react';
import { useAuth } from '@chainlit/react-client';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { profileState } from 'state/user';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

// Modul options based on smer - using keys that map to translation paths
const ISIT_MODULI = [
  { key: 'it', path: 'wizard.form.modul.isit.it' },
  { key: 'is', path: 'wizard.form.modul.isit.is' },
  { key: 'ie', path: 'wizard.form.modul.isit.ie' },
  { key: 'ba', path: 'wizard.form.modul.isit.ba' },
  { key: 'se', path: 'wizard.form.modul.isit.se' },
  { key: 'eb', path: 'wizard.form.modul.isit.eb' }
];

const MENADZMENT_MODULI = [
  { key: 'fm', path: 'wizard.form.modul.menadzment.fm' },
  { key: 'lo', path: 'wizard.form.modul.menadzment.lo' },
  { key: 'mk', path: 'wizard.form.modul.menadzment.mk' },
  { key: 'qs', path: 'wizard.form.modul.menadzment.qs' },
  { key: 'om', path: 'wizard.form.modul.menadzment.om' },
  { key: 'pm', path: 'wizard.form.modul.menadzment.pm' }
];

interface WizardModalProps {
  open: boolean;
  onComplete: () => void;
}

export function WizardModal({ open, onComplete }: WizardModalProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const setProfile = useSetRecoilState(profileState);
  const [step, setStep] = useState<'welcome' | 'form'>('welcome');
  const [displayName, setDisplayName] = useState('');
  const [year, setYear] = useState('');
  const [smer, setSmer] = useState('');
  const [modul, setModul] = useState('');
  const [customModul, setCustomModul] = useState('');
  const [akreditacija, setAkreditacija] = useState('');
  const [godinaStudija, setGodinaStudija] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFields, setShowFields] = useState({
    name: false,
    year: false,
    smer: false,
    modul: false,
    akreditacija: false,
    godinaStudija: false
  });

  // Auto-transition from welcome to form
  useEffect(() => {
    if (open && step === 'welcome') {
      const timer = setTimeout(() => {
        setStep('form');
      }, 2500); // Show welcome for 2.5 seconds
      return () => clearTimeout(timer);
    }
  }, [open, step]);

  // Staggered field animations with smoother delays
  useEffect(() => {
    if (step === 'form') {
      const timers = [
        setTimeout(() => setShowFields(prev => ({ ...prev, name: true })), 600),
        setTimeout(() => setShowFields(prev => ({ ...prev, year: true })), 900),
        setTimeout(() => setShowFields(prev => ({ ...prev, godinaStudija: true })), 1200),
        setTimeout(() => setShowFields(prev => ({ ...prev, akreditacija: true })), 1500),
        setTimeout(() => setShowFields(prev => ({ ...prev, smer: true })), 1800),
        setTimeout(() => setShowFields(prev => ({ ...prev, modul: true })), 2100),
      ];
      return () => timers.forEach(timer => clearTimeout(timer));
    }
  }, [step]);

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/wizard/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user?.identifier,
          skip: true,
          display_name: '',
          year: '',
          smer: '',
          modul: '',
          akreditacija: '',
          godina_studija: '',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('wizard.form.errors.saveFailed'));
      }

      if (data.profile) {
        setProfile(data.profile);
      }

      onComplete();
    } catch (error: any) {
      console.error('Error skipping wizard:', error);
      toast.error(error.message || t('wizard.form.errors.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !displayName ||
      !year ||
      !smer ||
      !akreditacija ||
      !godinaStudija ||
      (moduleRequired && !hasRequiredModule)
    ) {
      toast.error(t('wizard.form.errors.requiredFields'));
      return;
    }

    // Get full translated texts for storage
    const yearText = t(`wizard.form.year.options.${year.toLowerCase()}`);
    const smerText = t(`wizard.form.smer.options.${smer.toLowerCase()}`);
    const akreditacijaText = t(`wizard.form.akreditacija.options.${akreditacija}`);

    // For modul - get full text if it's a key, otherwise use custom text
    let modulText = '';
    // For OAS with 2023 accreditation (nova), use predefined modules
    if (year === 'OAS' && akreditacija === 'nova' && modul) {
      const modulOption = getModulOptions().find(m => m.key === modul);
      modulText = modulOption ? t(modulOption.path) : '';
    }
    // For OAS with 2014 (stara), MAS, or SAS - use custom text
    else if (customModul) {
      modulText = customModul;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/wizard/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user?.identifier,
          skip: false,
          display_name: displayName,
          year: yearText,
          smer: smerText,
          modul: modulText,
          akreditacija: akreditacijaText,
          godina_studija: godinaStudija,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('wizard.form.errors.saveFailed'));
      }

      if (data.profile) {
        setProfile(data.profile);
      }

      toast.success(t('wizard.form.success.saved'));

      // Trigger profile update event so other components can refresh
      window.dispatchEvent(new Event('profile-updated'));

      onComplete();
    } catch (error: any) {
      console.error('Error completing wizard:', error);
      toast.error(error.message || t('wizard.form.errors.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getModulOptions = () => {
    if (smer === 'ISIT') return ISIT_MODULI;
    if (smer === 'Menadzment') return MENADZMENT_MODULI;
    return [];
  };

  const getGodinaStudijaOptions = () => {
    if (year === 'OAS') return ['1', '2', '3', '4'];
    if (year === 'MAS' || year === 'SAS') return ['1', '2'];
    return [];
  };

  // Show predefined module select only for OAS + 2023 accreditation (nova)
  const showModulSelect = year === 'OAS' && akreditacija === 'nova' && (smer === 'ISIT' || smer === 'Menadzment');
  // Show custom module input for MAS, SAS, or OAS with 2014 accreditation (stara)
  const showCustomModul = ((year === 'MAS' || year === 'SAS') && smer) || (year === 'OAS' && akreditacija === 'stara' && smer);
  const moduleRequired = Number(godinaStudija) >= 2;
  const hasRequiredModule = showModulSelect
    ? Boolean(modul)
    : Boolean(customModul.trim());

  // Auto-scroll when modul field appears after smer selection
  useEffect(() => {
    if (smer && (showModulSelect || showCustomModul)) {
      setTimeout(() => {
        const modulField = document.getElementById(showModulSelect ? 'modul' : 'custom-modul');
        if (modulField) {
          modulField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  }, [smer, showModulSelect, showCustomModul]);

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateY(-10px);
            max-height: 0;
            margin-bottom: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 200px;
            margin-bottom: 1.5rem;
          }
        }

        @keyframes expandHeight {
          from {
            opacity: 0;
            max-height: 0;
            margin-bottom: 0;
          }
          to {
            opacity: 1;
            max-height: 200px;
            margin-bottom: 1.5rem;
          }
        }

        .wizard-fade-in {
          animation: fadeIn 1s ease-out;
        }

        .wizard-slide-up {
          animation: slideInUp 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        .wizard-slide-left {
          animation: slideInLeft 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .wizard-expand {
          animation: expandHeight 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .wizard-welcome-title {
          animation: slideInUp 0.8s ease-out 0.3s both;
        }

        .wizard-welcome-subtitle {
          animation: slideInUp 0.8s ease-out 0.8s both;
        }

        .wizard-scroll-container {
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
        }
      `}</style>

      <div className="fixed inset-0 z-50 bg-background overflow-hidden">
        {step === 'welcome' && (
          <div className="flex items-center justify-center h-full w-full wizard-fade-in">
            <div className="text-center space-y-6">
              <h1 className="text-6xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent wizard-welcome-title">
                {t('wizard.welcome.title')}
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl wizard-welcome-subtitle">
                {t('wizard.welcome.subtitle')}
              </p>
            </div>
          </div>
        )}

        {step === 'form' && (
          <div className="h-full w-full overflow-y-auto overflow-x-hidden wizard-fade-in wizard-scroll-container">
            <div className="w-full max-w-md mx-auto space-y-6 py-8 px-6">
              <div className="text-center space-y-2 wizard-slide-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                <h2 className="text-3xl font-semibold">
                  {t('wizard.form.title')}
                </h2>
                <p className="text-muted-foreground">
                  {t('wizard.form.subtitle')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-0 pb-24">
                {showFields.name && (
                  <div className="space-y-2 wizard-slide-left" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
                    <Label htmlFor="name">
                      {t('wizard.form.name.label')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder={t('wizard.form.name.placeholder')}
                      required
                      className="transition-all duration-300 focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}

                {showFields.year && (
                  <div className="space-y-2 wizard-slide-left" style={{ animationDelay: '0.15s', animationFillMode: 'both' }}>
                    <Label htmlFor="year">
                      {t('wizard.form.year.label')} <span className="text-red-500">*</span>
                    </Label>
                    <Select value={year} onValueChange={(val) => { setYear(val); setGodinaStudija(''); }} required>
                      <SelectTrigger id="year" className="transition-all duration-300">
                        <SelectValue placeholder={t('wizard.form.year.placeholder')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px] md:max-h-96">
                        <SelectItem value="OAS">
                          {t('wizard.form.year.options.oas')}
                        </SelectItem>
                        <SelectItem value="MAS">
                          {t('wizard.form.year.options.mas')}
                        </SelectItem>
                        <SelectItem value="SAS">
                          {t('wizard.form.year.options.sas')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {showFields.godinaStudija && year && (
                  <div className="space-y-2 wizard-slide-left" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
                    <Label htmlFor="godina-studija">
                      {t('wizard.form.godinaStudija.label', 'Godina studija')} <span className="text-red-500">*</span>
                    </Label>
                    <Select value={godinaStudija} onValueChange={setGodinaStudija} required>
                      <SelectTrigger id="godina-studija" className="transition-all duration-300">
                        <SelectValue placeholder={t('wizard.form.godinaStudija.placeholder', 'Izaberi godinu')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px] md:max-h-96">
                        {getGodinaStudijaOptions().map((god) => (
                          <SelectItem key={god} value={god}>
                            {god}. {t('wizard.form.godinaStudija.godina', 'godina')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {showFields.akreditacija && (
                  <div className="space-y-2 wizard-slide-left" style={{ animationDelay: '0.25s', animationFillMode: 'both' }}>
                    <Label htmlFor="akreditacija">
                      {t('wizard.form.akreditacija.label')} <span className="text-red-500">*</span>
                    </Label>
                    <Select value={akreditacija} onValueChange={setAkreditacija} required>
                      <SelectTrigger id="akreditacija" className="transition-all duration-300">
                        <SelectValue placeholder={t('wizard.form.akreditacija.placeholder')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px] md:max-h-96">
                        <SelectItem value="nova">
                          {t('wizard.form.akreditacija.options.nova')}
                        </SelectItem>
                        <SelectItem value="stara">
                          {t('wizard.form.akreditacija.options.stara')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {showFields.smer && (
                  <div className="space-y-2 wizard-slide-left" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
                    <Label htmlFor="smer">
                      {t('wizard.form.smer.label')} <span className="text-red-500">*</span>
                    </Label>
                    <Select value={smer} onValueChange={(val) => { setSmer(val); setModul(''); setCustomModul(''); }} required>
                      <SelectTrigger id="smer" className="transition-all duration-300">
                        <SelectValue placeholder={t('wizard.form.smer.placeholder')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px] md:max-h-96">
                        <SelectItem value="ISIT">
                          {t('wizard.form.smer.options.isit')}
                        </SelectItem>
                        <SelectItem value="Menadzment">
                          {t('wizard.form.smer.options.menadzment')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {showFields.modul && showModulSelect && (
                  <div className="space-y-2 wizard-expand" style={{ animationFillMode: 'both' }}>
                    <Label htmlFor="modul">
                      {moduleRequired
                        ? t('wizard.annual.module.requiredLabel')
                        : t('wizard.form.modul.labelOptional')}
                    </Label>
                    <Select value={modul} onValueChange={setModul}>
                      <SelectTrigger id="modul" className="transition-all duration-300">
                        <SelectValue placeholder={t('wizard.form.modul.placeholder')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px] md:max-h-96">
                        {getModulOptions().map((option) => (
                          <SelectItem key={option.key} value={option.key}>
                            {t(option.path)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {showFields.modul && showCustomModul && (
                  <div className="space-y-2 wizard-expand" style={{ animationFillMode: 'both' }}>
                    <Label htmlFor="custom-modul">
                      {moduleRequired
                        ? t('wizard.annual.module.requiredLabel')
                        : t('wizard.form.modul.labelOptional')}
                    </Label>
                    <Input
                      id="custom-modul"
                      value={customModul}
                      onChange={(e) => setCustomModul(e.target.value)}
                      placeholder={t('wizard.form.modul.placeholderCustom')}
                      className="transition-all duration-300 focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}

                <div className="pt-4 space-y-3 wizard-slide-up" style={{ animationDelay: '0.5s', animationFillMode: 'both' }}>
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      (moduleRequired && !hasRequiredModule)
                    }
                    className="w-full h-12 text-lg font-medium transition-all duration-300 hover:scale-[1.02]"
                  >
                    {isSubmitting ? t('wizard.form.actions.saving') : t('wizard.form.actions.save')}
                  </Button>

                  {/* Skip button below submit button */}
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleSkip}
                    disabled={isSubmitting}
                    className="fongpt-hover-text w-full text-sm text-secondary"
                  >
                    {t('wizard.form.actions.skip')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
